import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../SafeIcon';
import supabase from '../../../supabase/supabase';
import { compressImage } from '../../../utils/imageCompression';

const {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiImage, FiAlertCircle,
  FiArrowLeft, FiCamera, FiUploadCloud, FiCheck, FiX,
  FiCheckCircle, FiInfo, FiDatabase, FiCopy, FiStar,
  FiBold, FiItalic, FiList, FiTag
} = FiIcons;

const ACCENT = '#c8f542';
const TABLE = 'store_products_1772955957776';

export const BADGE_COLORS = {
  "Manager's Choice": { bg: '#7c3aed', text: '#ffffff' },
  'Best Seller':      { bg: '#f59e0b', text: '#ffffff' },
  'New Arrival':      { bg: '#10b981', text: '#ffffff' },
  'Hot Deal':         { bg: '#ef4444', text: '#ffffff' },
  'Limited Edition':  { bg: '#1d4ed8', text: '#ffffff' },
  'Staff Pick':       { bg: '#0891b2', text: '#ffffff' },
  'Top Rated':        { bg: '#d97706', text: '#ffffff' },
  'Trending':         { bg: '#db2777', text: '#ffffff' },
};

export const BADGE_PRESETS = Object.keys(BADGE_COLORS);

export const getBadgeStyle = (badge) => {
  if (!badge) return null;
  return BADGE_COLORS[badge] || { bg: '#374151', text: '#ffffff' };
};

const REQUIRED_SQL = `-- Run ALL of these in your Supabase SQL Editor:

DO $$ BEGIN
  IF NOT EXISTS (SELECT 1 FROM information_schema.columns WHERE table_name = 'store_products_1772955957776' AND column_name = 'images') THEN
    ALTER TABLE store_products_1772955957776 ADD COLUMN images jsonb NOT NULL DEFAULT '[]'::jsonb;
  END IF;
END $$;

DO $$ BEGIN
  IF NOT EXISTS (SELECT 1 FROM information_schema.columns WHERE table_name = 'store_products_1772955957776' AND column_name = 'badge') THEN
    ALTER TABLE store_products_1772955957776 ADD COLUMN badge text NOT NULL DEFAULT '';
  END IF;
END $$;

DO $$ BEGIN
  IF NOT EXISTS (SELECT 1 FROM information_schema.columns WHERE table_name = 'store_products_1772955957776' AND column_name = 'cost_price') THEN
    ALTER TABLE store_products_1772955957776 ADD COLUMN cost_price numeric(10,2) NOT NULL DEFAULT 0;
  END IF;
END $$;`;

const emptyProduct = {
  name: '', description: '', price: '', cost_price: '', stock: '',
  image_url: '', images: [], is_active: true, sku: '',
  on_sale: false, product_url: '', category: '', badge: ''
};

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ notification, onClose, isDark }) => {
  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => onClose(), notification.duration || 4000);
    return () => clearTimeout(t);
  }, [notification, onClose]);

  if (!notification) return null;

  const styles = {
    success: isDark
      ? 'bg-emerald-900/50 text-emerald-300 border-emerald-700/50'
      : 'bg-emerald-50 text-emerald-800 border-emerald-200',
    error: isDark
      ? 'bg-red-900/50 text-red-300 border-red-700/50'
      : 'bg-red-50 text-red-800 border-red-200',
    warning: isDark
      ? 'bg-amber-900/50 text-amber-300 border-amber-700/50'
      : 'bg-amber-50 text-amber-800 border-amber-200',
  };
  const icons = { success: FiCheckCircle, error: FiAlertCircle, warning: FiInfo };
  const Icon = icons[notification.type] || FiInfo;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      className={`fixed top-6 left-1/2 -translate-x-1/2 z-[200] flex items-start shadow-2xl rounded-2xl p-4 space-x-3 border max-w-md w-full ${styles[notification.type]}`}
    >
      <SafeIcon icon={Icon} className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1">
        <h4 className="text-sm font-bold">{notification.title}</h4>
        {notification.message && <p className="text-xs mt-1 leading-relaxed opacity-90">{notification.message}</p>}
      </div>
      <button onClick={onClose} className="p-1 hover:bg-black/10 rounded-lg transition-colors shrink-0">
        <SafeIcon icon={FiX} className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// ─── Rich Text Editor ─────────────────────────────────────────────────────────
const RichTextEditor = ({ value, onChange, isDark }) => {
  const editorRef = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !initialized.current) {
      editorRef.current.innerHTML = value || '';
      initialized.current = true;
    }
  }, []);

  const exec = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    onChange(editorRef.current?.innerHTML || '');
  };

  const tools = [
    { icon: FiBold, cmd: 'bold', title: 'Bold' },
    { icon: FiItalic, cmd: 'italic', title: 'Italic' },
    { icon: FiList, cmd: 'insertUnorderedList', title: 'Bullet List' },
  ];

  const wrapBorder = isDark
    ? 'border-[#334155] focus-within:border-[#c8f542] focus-within:ring-[#c8f542]/20'
    : 'border-gray-200 focus-within:border-[#c8f542] focus-within:ring-[#c8f542]/20';
  const toolbarBg = isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-gray-50 border-gray-100';
  const btnCls = isDark
    ? 'text-gray-400 hover:bg-[#334155] hover:text-white'
    : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800';
  const editorColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const placeholder = isDark ? '#475569' : '#9ca3af';

  return (
    <div className={`border rounded-lg overflow-hidden focus-within:ring-2 transition-all ${wrapBorder}`}>
      <div className={`flex items-center space-x-1 px-3 py-2 border-b ${toolbarBg}`}>
        {tools.map(({ icon, cmd, title }) => (
          <button key={cmd} type="button"
            onMouseDown={e => { e.preventDefault(); exec(cmd); }}
            title={title}
            className={`p-1.5 rounded transition-colors ${btnCls}`}>
            <SafeIcon icon={icon} className="w-3.5 h-3.5" />
          </button>
        ))}
        <div className={`w-px h-4 mx-1 ${isDark ? 'bg-[#334155]' : 'bg-gray-200'}`} />
        <button type="button"
          onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}
          title="Clear formatting"
          className={`p-1.5 rounded transition-colors text-[10px] font-bold ${btnCls}`}>
          Aa
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || '')}
        onBlur={() => onChange(editorRef.current?.innerHTML || '')}
        className={`w-full min-h-[140px] p-4 text-sm outline-none leading-relaxed ${editorColor} ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}
        style={{ whiteSpace: 'pre-wrap' }}
        data-placeholder="Write a compelling description..."
      />
      <style>{`[contenteditable]:empty:before{content:attr(data-placeholder);color:${placeholder};pointer-events:none}`}</style>
    </div>
  );
};

// ─── Multi-Image Uploader ─────────────────────────────────────────────────────
const MultiImageUploader = ({ images, onChange, setNotification, isDark }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItem = useRef(null);
  const fileInputRef = useRef(null);
  const moreInputRef = useRef(null);

  const uploadFile = async (file) => {
    const compressedFile = await compressImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.82 });
    const ext = (compressedFile.name || file.name).split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${ext}`;
    const { error } = await supabase.storage
      .from('product-images')
      .upload(fileName, compressedFile, { cacheControl: '3600', upsert: false });
    if (error) throw new Error(error.message || 'Upload failed');
    const { data } = supabase.storage.from('product-images').getPublicUrl(fileName);
    if (!data?.publicUrl) throw new Error('Could not get public URL');
    return data.publicUrl;
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const url = await uploadFile(file);
        if (url) urls.push(url);
      }
      onChange([...images, ...urls]);
      setNotification({ type: 'success', title: `${urls.length} image(s) uploaded`, duration: 2500 });
    } catch (err) {
      setNotification({ type: 'error', title: 'Upload Failed', message: err.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (moreInputRef.current) moreInputRef.current.value = '';
    }
  };

  const handleRemove = (idx) => onChange(images.filter((_, i) => i !== idx));
  const handleSetCover = (idx) => {
    if (idx === 0) return;
    const updated = [...images];
    const [item] = updated.splice(idx, 1);
    updated.unshift(item);
    onChange(updated);
  };

  const onDragStart = (e, idx) => { dragItem.current = idx; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOverIndex(idx); };
  const onDrop = (e, idx) => {
    e.preventDefault();
    if (dragItem.current === null || dragItem.current === idx) { setDragOverIndex(null); return; }
    const updated = [...images];
    const [dragged] = updated.splice(dragItem.current, 1);
    updated.splice(idx, 0, dragged);
    onChange(updated);
    dragItem.current = null;
    setDragOverIndex(null);
  };
  const onDragEnd = () => { dragItem.current = null; setDragOverIndex(null); };

  const cover = images[0];
  const rest = images.slice(1);

  const cardBg = isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100';
  const titleColor = isDark ? 'text-white' : 'text-gray-800';
  const emptyZoneCls = isDark
    ? 'border-[#334155] bg-[#0f172a] hover:border-[#c8f542]/50 hover:bg-[#c8f542]/5'
    : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/50';
  const thumbBorder = isDark ? 'border-[#334155] hover:border-gray-500' : 'border-gray-200 hover:border-gray-300';
  const addThumbCls = isDark
    ? 'border-[#334155] hover:border-[#c8f542]/60 hover:bg-[#c8f542]/5'
    : 'border-gray-200 hover:border-[#c8f542] hover:bg-[#c8f542]/5';

  return (
    <div className={`rounded-xl shadow-sm border p-5 space-y-4 ${cardBg}`}>
      <div className="flex items-center justify-between">
        <h3 className={`text-base font-semibold ${titleColor}`}>Product Images</h3>
        {images.length > 0 && (
          <span className="text-xs text-gray-400">{images.length} photo{images.length !== 1 ? 's' : ''} · drag to reorder</span>
        )}
      </div>

      {images.length === 0 ? (
        <label
          className={`w-full h-48 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors ${uploading ? 'border-[#c8f542] bg-[#c8f542]/10' : emptyZoneCls}`}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        >
          <SafeIcon icon={uploading ? FiUploadCloud : FiCamera}
            className={`w-8 h-8 mb-3 ${uploading ? 'text-[#c8f542] animate-bounce' : 'text-gray-400'}`} />
          <span className={`text-sm font-semibold ${uploading ? 'text-[#c8f542]' : 'text-gray-400'}`}>
            {uploading ? 'Uploading...' : 'Click or drag to add images'}
          </span>
          <span className="text-xs text-gray-500 mt-1">First image becomes the cover</span>
          <input ref={fileInputRef} type="file" className="hidden" accept="image/*" multiple
            onChange={e => handleFiles(e.target.files)} disabled={uploading} />
        </label>
      ) : (
        <div className="space-y-3">
          <div
            draggable
            onDragStart={e => onDragStart(e, 0)} onDragOver={e => onDragOver(e, 0)}
            onDrop={e => onDrop(e, 0)} onDragEnd={onDragEnd}
            className={`relative w-full rounded-xl overflow-hidden border-2 group cursor-grab active:cursor-grabbing transition-all ${dragOverIndex === 0 ? 'border-dashed border-[#c8f542] opacity-60' : 'border-[#c8f542] shadow-md shadow-[#c8f542]/10'}`}
            style={{ aspectRatio: '16/9' }}
          >
            <img src={cover} alt="Cover" className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3 bg-[#c8f542] text-black text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center space-x-1 shadow-lg">
              <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /><span>Cover Photo</span>
            </div>
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <button onClick={() => handleRemove(0)}
                className="bg-red-500/90 hover:bg-red-500 text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center space-x-1.5 shadow">
                <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" /><span>Remove</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {rest.map((url, i) => {
              const realIdx = i + 1;
              return (
                <div key={`${url}-${realIdx}`} draggable
                  onDragStart={e => onDragStart(e, realIdx)} onDragOver={e => onDragOver(e, realIdx)}
                  onDrop={e => onDrop(e, realIdx)} onDragEnd={onDragEnd}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 group cursor-grab active:cursor-grabbing transition-all ${dragOverIndex === realIdx ? 'border-dashed border-[#c8f542] scale-95 opacity-60' : thumbBorder}`}>
                  <img src={url} alt={`Photo ${realIdx + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center space-y-1 p-1">
                    <button onClick={() => handleSetCover(realIdx)}
                      className="bg-white hover:bg-[#c8f542] text-gray-900 hover:text-black text-[9px] font-bold px-2 py-1 rounded flex items-center space-x-0.5 w-full justify-center transition-colors">
                      <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /><span>Cover</span>
                    </button>
                    <button onClick={() => handleRemove(realIdx)}
                      className="bg-red-500/90 hover:bg-red-500 text-white text-[9px] font-bold px-2 py-1 rounded flex items-center space-x-0.5 w-full justify-center">
                      <SafeIcon icon={FiTrash2} className="w-2.5 h-2.5" /><span>Remove</span>
                    </button>
                  </div>
                </div>
              );
            })}
            <label
              className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${addThumbCls}`}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            >
              {uploading
                ? <div className="w-5 h-5 border-2 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin" />
                : <><SafeIcon icon={FiPlus} className="w-5 h-5 text-gray-400 mb-0.5" /><span className="text-[9px] text-gray-400 font-medium">Add</span></>
              }
              <input ref={moreInputRef} type="file" className="hidden" accept="image/*" multiple
                onChange={e => handleFiles(e.target.files)} disabled={uploading} />
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Column Check ─────────────────────────────────────────────────────────────
const useColumnCheck = () => {
  const [missing, setMissing] = useState([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const check = async () => {
      const { error } = await supabase.from(TABLE).select('id, images, badge, cost_price').limit(1);
      if (error) {
        const msg = error.message || '';
        const cols = [];
        if (msg.includes('images')) cols.push('images');
        if (msg.includes('badge')) cols.push('badge');
        if (msg.includes('cost_price')) cols.push('cost_price');
        setMissing(cols.length ? cols : ['images', 'badge', 'cost_price']);
      }
      setChecked(true);
    };
    check();
  }, []);

  return { missing, checked };
};

// ─── Badge Picker ─────────────────────────────────────────────────────────────
const BadgePicker = ({ value, onChange, isDark }) => {
  const badgeStyle = value ? getBadgeStyle(value) : null;
  const cardBg = isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100';
  const titleColor = isDark ? 'text-white' : 'text-gray-800';
  const inputBg = isDark
    ? 'bg-[#0f172a] border-[#334155] text-white placeholder-gray-500 focus:border-[#c8f542] focus:ring-[#c8f542]/20'
    : 'bg-white border-gray-200 text-gray-800 focus:border-[#c8f542] focus:ring-[#c8f542]/20';
  const swatchText = isDark ? 'text-gray-300' : 'text-gray-700';
  const swatchBorder = isDark ? 'hover:border-gray-500' : 'hover:border-gray-300';
  const previewBg = isDark ? 'bg-[#0f172a] border-[#334155]' : 'bg-gray-100 border-gray-200';

  return (
    <div className={`rounded-xl shadow-sm border p-5 space-y-4 ${cardBg}`}>
      <div className="flex items-center space-x-2">
        <SafeIcon icon={FiTag} className="w-4 h-4 text-[#c8f542]" />
        <h3 className={`text-base font-semibold ${titleColor}`}>Product Badge</h3>
      </div>
      <p className="text-xs text-gray-400">Displayed on the top-left of the product image.</p>

      <div className="flex items-center space-x-2">
        <input
          type="text" value={value}
          onChange={e => onChange(e.target.value)}
          className={`flex-1 p-3 border rounded-lg outline-none focus:ring-2 text-sm transition-all ${inputBg}`}
          placeholder="Custom badge text..."
        />
        {value && (
          <button type="button" onClick={() => onChange('')}
            className="p-2.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all">
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {BADGE_PRESETS.map(preset => {
          const col = BADGE_COLORS[preset];
          const isSelected = value === preset;
          return (
            <button key={preset} type="button"
              onClick={() => onChange(isSelected ? '' : preset)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all text-left ${
                isSelected
                  ? 'border-[#c8f542] shadow-md scale-[1.02]'
                  : `border-transparent ${swatchBorder}`
              }`}
              style={{ backgroundColor: col.bg + '20' }}
            >
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: col.bg }} />
              <span className={`text-[11px] font-bold leading-tight ${swatchText}`}>{preset}</span>
              {isSelected && <SafeIcon icon={FiCheck} className="w-3 h-3 text-[#c8f542] ml-auto shrink-0" />}
            </button>
          );
        })}
      </div>

      {value && badgeStyle && (
        <div className="pt-2">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Preview on product</p>
          <div className={`relative w-full h-24 rounded-xl overflow-hidden border ${previewBg}`}>
            <div className="absolute inset-0 flex items-center justify-center">
              <SafeIcon icon={FiImage} className={`w-8 h-8 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            </div>
            <div
              className="absolute top-3 left-3 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg"
              style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text }}
            >
              {value}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Product Editor ───────────────────────────────────────────────────────────
const ProductEditor = ({ product, products = [], projectId, onBack, onSaveComplete, columnsMissing, isDark }) => {
  const normalizeImages = (p) => {
    if (!p) return [];
    if (Array.isArray(p.images) && p.images.length > 0) return p.images;
    if (p.image_url) return [p.image_url];
    return [];
  };

  const [form, setForm] = useState({
    ...emptyProduct,
    ...product,
    images: normalizeImages(product),
    badge: product?.badge || '',
    cost_price: product?.cost_price != null ? String(product.cost_price) : '',
    price: product?.price != null ? String(product.price) : '',
    stock: product?.stock != null ? String(product.stock) : '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [categoryInput, setCategoryInput] = useState('');

  const existingCategories = Array.from(new Set(
    products.flatMap(p => (p.category || '').split(',').map(c => c.trim()).filter(Boolean))
  )).sort();
  const currentCategories = (form.category || '').split(',').map(c => c.trim()).filter(Boolean);
  const isEdit = !!product?.id;

  // Dark mode tokens
  const pageBg = isDark ? 'bg-[#0f172a]' : 'bg-[#f8f9fa]';
  const cardBg = isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100';
  const headerBg = isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-200';
  const titleColor = isDark ? 'text-white' : 'text-gray-800';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const subColor = isDark ? 'text-gray-400' : 'text-gray-400';
  const divider = isDark ? 'border-[#334155]' : 'border-gray-50';

  const inputCls = (field) => {
    const darkBase = isDark
      ? 'bg-[#0f172a] border-[#334155] text-white placeholder-gray-500 focus:border-[#c8f542] focus:ring-[#c8f542]/20'
      : 'bg-white border-gray-200 text-gray-800 focus:border-[#c8f542] focus:ring-[#c8f542]/20';
    const base = `w-full p-3 border rounded-lg outline-none transition-all text-sm focus:ring-2 ${darkBase} `;
    if (errors[field]) return base + (isDark ? 'border-red-500 text-red-300' : 'border-red-400 text-red-900');
    return base;
  };

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = 'Product title is required';
    if (form.price === '' || isNaN(Number(form.price)) || Number(form.price) < 0) e.price = 'Enter a valid price';
    if (form.stock === '' || isNaN(Number(form.stock)) || Number(form.stock) < 0) e.stock = 'Enter a valid stock number';
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setNotification({ type: 'error', title: 'Validation Error', message: 'Please fix the highlighted fields.', duration: 3500 });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    let finalCategory = form.category || '';
    if (categoryInput.trim()) {
      const v = categoryInput.trim();
      if (!currentCategories.includes(v)) finalCategory = [...currentCategories, v].join(',');
    }
    setSaving(true);
    setNotification(null);
    const imgs = Array.isArray(form.images) ? form.images : [];
    const coverUrl = imgs[0] || '';
    const basePayload = {
      name: form.name.trim(), description: form.description || '',
      price: parseFloat(form.price) || 0, stock: parseInt(form.stock, 10) || 0,
      image_url: coverUrl, is_active: form.is_active !== false,
      sku: form.sku || '', on_sale: form.on_sale || false,
      product_url: form.product_url || '', category: finalCategory,
    };
    const newColPayload = {};
    if (!columnsMissing.includes('images')) newColPayload.images = imgs;
    if (!columnsMissing.includes('badge')) newColPayload.badge = form.badge || '';
    if (!columnsMissing.includes('cost_price')) newColPayload.cost_price = parseFloat(form.cost_price) || 0;
    const payload = { ...basePayload, ...newColPayload };
    if (projectId) payload.project_id = projectId;
    try {
      if (isEdit) {
        payload.updated_at = new Date().toISOString();
        const { error } = await supabase.from(TABLE).update(payload).eq('id', product.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from(TABLE).insert(payload).select().single();
        if (error) throw error;
        if (!data) throw new Error('Insert returned no data.');
      }
      setNotification({ type: 'success', title: isEdit ? 'Product Updated!' : 'Product Created!', duration: 1800 });
      setTimeout(() => onSaveComplete(), 1600);
    } catch (err) {
      const detail = err?.details ? ` | ${err.details}` : '';
      const hint = err?.hint ? ` | Hint: ${err.hint}` : '';
      setNotification({ type: 'error', title: 'Save Failed', message: `${err?.message || 'Unknown error'}${detail}${hint}`, duration: 12000 });
    } finally {
      setSaving(false);
    }
  };

  const handleCatKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const v = categoryInput.trim();
    if (v && !currentCategories.includes(v)) setForm(f => ({ ...f, category: [...currentCategories, v].join(',') }));
    setCategoryInput('');
  };
  const handleCatBlur = () => {
    const v = categoryInput.trim();
    if (v && !currentCategories.includes(v)) setForm(f => ({ ...f, category: [...currentCategories, v].join(',') }));
    setCategoryInput('');
  };
  const toggleCat = (cat) => {
    setForm(f => ({
      ...f,
      category: currentCategories.includes(cat)
        ? currentCategories.filter(c => c !== cat).join(',')
        : [...currentCategories, cat].join(',')
    }));
  };

  const currencyPrefix = isDark ? 'text-gray-400' : 'text-gray-400';

  return (
    <div className={`flex flex-col h-full overflow-hidden transition-colors ${pageBg}`}>
      <AnimatePresence>
        {notification && <Toast notification={notification} onClose={() => setNotification(null)} isDark={isDark} />}
      </AnimatePresence>

      {columnsMissing.length > 0 && (
        <div className="bg-amber-900/30 border-b border-amber-700/50 px-6 py-3 flex items-center space-x-3 shrink-0">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-300 font-medium">
            Missing DB columns: <strong>{columnsMissing.join(', ')}</strong> — run the SQL migration. Basic fields still save.
          </p>
        </div>
      )}

      {/* Header */}
      <div className={`border-b px-6 py-4 flex items-center justify-between shrink-0 shadow-sm z-10 transition-colors ${headerBg}`}>
        <div className="flex items-center space-x-4">
          <button onClick={onBack} disabled={saving}
            className={`p-2 rounded-full transition-colors disabled:opacity-50 ${isDark ? 'hover:bg-[#334155] text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <h1 className={`text-xl font-bold ${titleColor}`}>{isEdit ? 'Edit Product' : 'Add Product'}</h1>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={onBack} disabled={saving}
            className={`px-5 py-2 font-medium rounded-lg transition-colors text-sm disabled:opacity-50 ${isDark ? 'text-gray-400 hover:bg-[#334155]' : 'text-gray-600 hover:bg-gray-100'}`}>
            Discard
          </button>
          <button
            onClick={handleSave} disabled={saving}
            className="text-black px-6 py-2 rounded-lg font-bold text-sm transition-colors shadow-sm disabled:opacity-70 flex items-center space-x-2 min-w-[130px] justify-center hover:brightness-110"
            style={{ backgroundColor: ACCENT }}
          >
            {saving
              ? <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /><span>Saving...</span></>
              : <span>{isEdit ? 'Save Changes' : 'Save Product'}</span>
            }
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
        <div className="max-w-6xl mx-auto grid grid-cols-3 gap-8">

          {/* LEFT */}
          <div className="col-span-2 space-y-8">
            {/* Title & Description */}
            <div className={`rounded-xl shadow-sm border p-6 space-y-6 transition-colors ${cardBg}`}>
              <div>
                <label className={`text-sm font-medium block mb-2 ${labelColor}`}>
                  Product Title <span className="text-red-400">*</span>
                </label>
                <input type="text" value={form.name}
                  onChange={e => { setForm(f => ({ ...f, name: e.target.value })); if (errors.name) setErrors(v => ({ ...v, name: null })); }}
                  placeholder="Enter product name..."
                  className={inputCls('name').replace('text-sm', 'text-base')} />
                <AnimatePresence>
                  {errors.name && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                      className="text-red-400 text-xs mt-1.5 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="w-3 h-3 mr-1" />{errors.name}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
              <div>
                <label className={`text-sm font-medium block mb-2 ${labelColor}`}>Description</label>
                <RichTextEditor value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} isDark={isDark} />
              </div>
            </div>

            {/* Pricing */}
            <div className={`rounded-xl shadow-sm border p-6 space-y-5 transition-colors ${cardBg}`}>
              <h3 className={`text-base font-semibold ${titleColor}`}>Pricing</h3>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className={`text-sm font-medium block mb-2 ${labelColor}`}>
                    Selling Price <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className={`absolute left-4 top-1/2 -translate-y-1/2 font-medium text-sm ${currencyPrefix}`}>R</span>
                    <input type="number" min="0" step="0.01" value={form.price}
                      onChange={e => { setForm(f => ({ ...f, price: e.target.value })); if (errors.price) setErrors(v => ({ ...v, price: null })); }}
                      className={`${inputCls('price')} pl-8`} placeholder="0.00" />
                  </div>
                  <AnimatePresence>
                    {errors.price && (
                      <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        className="text-red-400 text-xs mt-1.5 flex items-center">
                        <SafeIcon icon={FiAlertCircle} className="w-3 h-3 mr-1" />{errors.price}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <div>
                  <label className={`text-sm font-medium block mb-2 ${labelColor}`}>
                    Cost Price <span className={`text-xs font-normal ${subColor}`}>(margin tracking)</span>
                  </label>
                  <div className="relative">
                    <span className={`absolute left-4 top-1/2 -translate-y-1/2 font-medium text-sm ${currencyPrefix}`}>R</span>
                    <input type="number" min="0" step="0.01" value={form.cost_price}
                      onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
                      className={`${inputCls('')} pl-8`} placeholder="0.00" />
                  </div>
                  {Number(form.price) > 0 && Number(form.cost_price) > 0 && (
                    <p className="text-xs mt-1.5 text-emerald-400 font-medium">
                      Margin: R{(Number(form.price) - Number(form.cost_price)).toFixed(2)} ({((1 - Number(form.cost_price) / Number(form.price)) * 100).toFixed(0)}%)
                    </p>
                  )}
                </div>
              </div>
              <div className={`pt-2 border-t ${divider}`}>
                <label className="flex items-center space-x-3 cursor-pointer group w-max">
                  <div
                    onClick={() => setForm(f => ({ ...f, on_sale: !f.on_sale }))}
                    className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${form.on_sale ? 'bg-[#c8f542] border-[#c8f542]' : (isDark ? 'border-gray-600 group-hover:border-[#c8f542]' : 'border-gray-300 group-hover:border-[#c8f542]')}`}
                  >
                    {form.on_sale && <div className="w-2.5 h-2.5 bg-black rounded-full" />}
                  </div>
                  <span className={`text-sm ${labelColor}`}>Mark as On Sale</span>
                </label>
              </div>
            </div>

            {/* Inventory */}
            <div className={`rounded-xl shadow-sm border p-6 space-y-5 transition-colors ${cardBg}`}>
              <h3 className={`text-base font-semibold ${titleColor}`}>Inventory</h3>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className={`text-sm font-medium block mb-2 ${labelColor}`}>
                    Stock Quantity <span className="text-red-400">*</span>
                  </label>
                  <input type="number" min="0" value={form.stock}
                    onChange={e => { setForm(f => ({ ...f, stock: e.target.value })); if (errors.stock) setErrors(v => ({ ...v, stock: null })); }}
                    className={inputCls('stock')} placeholder="0" />
                  <AnimatePresence>
                    {errors.stock && (
                      <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        className="text-red-400 text-xs mt-1.5 flex items-center">
                        <SafeIcon icon={FiAlertCircle} className="w-3 h-3 mr-1" />{errors.stock}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <div>
                  <label className={`text-sm font-medium block mb-2 ${labelColor}`}>SKU</label>
                  <input type="text" value={form.sku}
                    onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
                    className={inputCls('')} placeholder="e.g. TSHIRT-BLK-M" />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="col-span-1 space-y-6">
            <MultiImageUploader
              images={form.images || []}
              onChange={imgs => setForm(f => ({ ...f, images: imgs }))}
              setNotification={setNotification}
              isDark={isDark}
            />

            <BadgePicker value={form.badge || ''} onChange={v => setForm(f => ({ ...f, badge: v }))} isDark={isDark} />

            {/* Categories */}
            <div className={`rounded-xl shadow-sm border p-5 space-y-4 transition-colors ${cardBg}`}>
              <h3 className={`text-base font-semibold ${titleColor}`}>Categories</h3>
              <input type="text" value={categoryInput}
                onChange={e => setCategoryInput(e.target.value)}
                onKeyDown={handleCatKeyDown} onBlur={handleCatBlur}
                className={inputCls('')} placeholder="Type & press Enter to add" />
              <AnimatePresence>
                {currentCategories.length > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-wrap gap-2">
                    {currentCategories.map(cat => (
                      <div key={cat} className="flex items-center space-x-1 bg-[#c8f542]/10 text-[#c8f542] px-2.5 py-1 rounded-lg text-xs font-bold border border-[#c8f542]/20">
                        <span>{cat}</span>
                        <button type="button" onMouseDown={e => { e.preventDefault(); toggleCat(cat); }}
                          className="hover:bg-[#c8f542]/20 p-0.5 rounded transition-colors">
                          <SafeIcon icon={FiX} className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              {existingCategories.length > 0 && (
                <div className={`pt-3 border-t ${divider}`}>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Quick Select</p>
                  <div className="flex flex-wrap gap-2">
                    {existingCategories.map(cat => {
                      const sel = currentCategories.includes(cat);
                      return (
                        <button key={cat} type="button"
                          onMouseDown={e => { e.preventDefault(); toggleCat(cat); setCategoryInput(''); }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${
                            sel
                              ? 'bg-[#c8f542] text-black border-[#c8f542]'
                              : isDark
                                ? 'bg-transparent text-gray-400 border-[#334155] hover:border-[#c8f542] hover:text-[#c8f542]'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-[#c8f542] hover:text-[#c8f542]'
                          }`}>
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Visibility & URL */}
            <div className={`rounded-xl shadow-sm border p-5 space-y-5 transition-colors ${cardBg}`}>
              <label className="flex items-center space-x-3 cursor-pointer group">
                <div
                  onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                  className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                    !form.is_active
                      ? 'bg-[#c8f542] border-[#c8f542]'
                      : isDark ? 'border-gray-600 group-hover:border-[#c8f542]' : 'border-gray-300 group-hover:border-[#c8f542]'
                  }`}
                >
                  {!form.is_active && <div className="w-2.5 h-2.5 bg-black rounded-full" />}
                </div>
                <span className={`text-sm ${labelColor}`}>Hidden Product</span>
              </label>
              <div>
                <label className={`text-sm font-medium block mb-2 ${labelColor}`}>Product Page URL</label>
                <input type="text" value={form.product_url}
                  onChange={e => setForm(f => ({ ...f, product_url: e.target.value }))}
                  className={inputCls('')} placeholder="/product-name" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main ProductsPanel ───────────────────────────────────────────────────────
export default function ProductsPanel({ projectId, isDark = true }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [globalNotification, setGlobalNotification] = useState(null);
  const [isDbMissing, setIsDbMissing] = useState(false);
  const [copiedSql, setCopiedSql] = useState(false);

  const { missing: columnsMissing, checked: columnsChecked } = useColumnCheck();

  useEffect(() => {
    if (view === 'list') fetchProducts();
  }, [view, projectId]);

  const fetchProducts = async () => {
    setLoading(true);
    setIsDbMissing(false);
    try {
      let q = supabase.from(TABLE).select('*').order('created_at', { ascending: false });
      if (projectId) q = q.eq('project_id', projectId);
      const { data, error } = await q;
      if (error) {
        if (error.code === '42P01') setIsDbMissing(true);
        else console.error('Fetch products error:', error);
        setProducts([]);
      } else {
        setProducts(data || []);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      let q = supabase.from(TABLE).delete().eq('id', id);
      if (projectId) q = q.eq('project_id', projectId);
      const { error } = await q;
      if (error) throw error;
      setProducts(p => p.filter(x => x.id !== id));
      setDeleteConfirm(null);
      setGlobalNotification({ type: 'success', title: 'Deleted', message: 'Product removed successfully.' });
    } catch (err) {
      setGlobalNotification({ type: 'error', title: 'Delete Failed', message: err.message });
    }
  };

  if (view === 'editor') {
    return (
      <ProductEditor
        product={editingProduct}
        products={products}
        projectId={projectId}
        columnsMissing={columnsMissing}
        isDark={isDark}
        onBack={() => { setView('list'); setEditingProduct(null); }}
        onSaveComplete={() => { setView('list'); setEditingProduct(null); fetchProducts(); }}
      />
    );
  }

  const filtered = products.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()));
  const getImg = (p) => (Array.isArray(p.images) && p.images.length > 0) ? p.images[0] : (p.image_url || '');

  // Dark mode tokens for list view
  const pageBg = isDark ? 'bg-[#0f172a]' : 'bg-[#fafbfc]';
  const titleColor = isDark ? 'text-white' : 'text-gray-800';
  const subColor = isDark ? 'text-gray-400' : 'text-gray-400';
  const cardBg = isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100';
  const theadBg = isDark ? 'bg-[#0f172a] border-[#334155]' : 'bg-gray-50 border-gray-100';
  const thColor = isDark ? 'text-gray-500' : 'text-gray-400';
  const trHover = isDark ? 'hover:bg-[#334155]/30' : 'hover:bg-gray-50/50';
  const tdDivide = isDark ? 'divide-[#334155]/50' : 'divide-gray-50';
  const cellText = isDark ? 'text-gray-200' : 'text-gray-800';
  const cellSub = isDark ? 'text-gray-500' : 'text-gray-400';
  const searchBg = isDark
    ? 'bg-[#1e293b] border-[#334155] text-white placeholder-gray-500 focus:border-[#c8f542]'
    : 'bg-white border-gray-200 text-gray-800 focus:border-[#c8f542]';
  const emptyCardBg = isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100';
  const emptyIconBg = isDark ? 'bg-[#0f172a]' : 'bg-gray-50';

  return (
    <div className={`absolute inset-0 overflow-y-auto p-8 custom-scrollbar transition-colors ${pageBg}`}>
      <AnimatePresence>
        {globalNotification && (
          <Toast notification={globalNotification} onClose={() => setGlobalNotification(null)} isDark={isDark} />
        )}
      </AnimatePresence>

      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${titleColor}`}>Products</h1>
            <p className={`text-sm mt-0.5 ${subColor}`}>{products.length} product{products.length !== 1 ? 's' : ''} total</p>
          </div>
          <button
            onClick={() => { if (!isDbMissing) { setEditingProduct(null); setView('editor'); } }}
            disabled={isDbMissing || !columnsChecked}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm flex items-center space-x-2 transition-all shadow-lg ${
              isDbMissing
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed shadow-none'
                : 'text-black hover:brightness-110 shadow-[#c8f542]/20'
            }`}
            style={!isDbMissing ? { backgroundColor: ACCENT } : {}}
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" /><span>Add Product</span>
          </button>
        </div>

        {/* Missing columns warning */}
        {columnsChecked && columnsMissing.length > 0 && (
          <div className={`border rounded-xl p-4 ${isDark ? 'bg-amber-900/20 border-amber-700/40' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-start space-x-3 mb-3">
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm font-bold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                  Missing database columns: <code className="font-mono">{columnsMissing.join(', ')}</code>
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  Products will save but these fields won't be stored until you run the SQL below.
                </p>
              </div>
            </div>
            <div className="relative">
              <button
                onClick={() => { navigator.clipboard.writeText(REQUIRED_SQL); setCopiedSql(true); setTimeout(() => setCopiedSql(false), 2000); }}
                className={`absolute top-3 right-3 z-10 flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${copiedSql ? 'bg-green-500 text-white' : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'}`}
              >
                <SafeIcon icon={copiedSql ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                <span>{copiedSql ? 'Copied!' : 'Copy'}</span>
              </button>
              <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg text-[10px] leading-relaxed overflow-x-auto font-mono">{REQUIRED_SQL}</pre>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm outline-none transition-colors shadow-sm ${searchBg}`} />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin" />
          </div>
        ) : isDbMissing ? (
          <div className={`rounded-2xl border shadow-sm p-8 text-center ${isDark ? 'bg-[#1e293b] border-red-900/50' : 'bg-white border-red-200'}`}>
            <SafeIcon icon={FiDatabase} className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className={`text-lg font-bold mb-1 ${titleColor}`}>Products table not found</h2>
            <p className={`text-sm ${subColor}`}>Run the initial store migration in your Supabase SQL Editor.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className={`text-center py-20 rounded-2xl border shadow-sm transition-colors ${emptyCardBg}`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${emptyIconBg}`}>
              <SafeIcon icon={FiImage} className={`w-8 h-8 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            </div>
            <p className={`font-medium ${titleColor}`}>{search ? 'No products match your search' : 'No products yet'}</p>
            <p className={`text-sm mt-1 ${subColor}`}>Click "Add Product" to get started</p>
          </div>
        ) : (
          <div className={`rounded-2xl border shadow-sm overflow-hidden transition-colors ${cardBg}`}>
            <table className="w-full">
              <thead className={`border-b transition-colors ${theadBg}`}>
                <tr>
                  {['Product', 'Badge', 'Price', 'Cost', 'Stock', 'Status', 'Actions'].map(h => (
                    <th key={h} className={`text-left text-[10px] font-black uppercase tracking-wider px-4 py-3 ${thColor}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${tdDivide}`}>
                {filtered.map(p => {
                  const badgeStyle = getBadgeStyle(p.badge);
                  return (
                    <tr key={p.id} className={`transition-colors ${trHover}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden shrink-0 border ${isDark ? 'bg-[#334155] border-[#475569]' : 'bg-gray-100 border-gray-200'}`}>
                            {getImg(p)
                              ? <img src={getImg(p)} alt={p.name} className="w-full h-full object-cover" />
                              : <SafeIcon icon={FiImage} className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-300'}`} />}
                          </div>
                          <div>
                            <div className={`font-medium text-sm ${cellText}`}>{p.name}</div>
                            {p.sku && <div className={`text-xs ${cellSub}`}>SKU: {p.sku}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {p.badge && badgeStyle
                          ? <span className="text-[10px] font-black px-2.5 py-1 rounded-full" style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text }}>{p.badge}</span>
                          : p.on_sale
                            ? <span className="bg-red-500/20 text-red-400 text-[10px] font-black px-2 py-1 rounded-full">Sale</span>
                            : <span className={`text-xs ${cellSub}`}>—</span>
                        }
                      </td>
                      <td className={`px-4 py-4 font-bold text-sm ${cellText}`}>R{Number(p.price || 0).toFixed(2)}</td>
                      <td className={`px-4 py-4 text-sm ${cellSub}`}>{Number(p.cost_price) > 0 ? `R${Number(p.cost_price).toFixed(2)}` : '—'}</td>
                      <td className="px-4 py-4 text-sm">
                        <span className={p.stock === 0 ? 'text-red-400 font-medium' : p.stock <= 5 ? 'text-amber-400 font-medium' : cellText}>
                          {p.stock}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${p.is_active ? 'bg-emerald-500/20 text-emerald-400' : (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')}`}>
                          {p.is_active ? 'Active' : 'Hidden'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-1">
                          <button onClick={() => { setEditingProduct(p); setView('editor'); }}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-500 hover:bg-[#c8f542]/10 hover:text-[#c8f542]' : 'text-gray-400 hover:bg-[#c8f542]/10 hover:text-[#c8f542]'}`}>
                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteConfirm(p)}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-500 hover:bg-red-500/10 hover:text-red-400' : 'text-gray-400 hover:bg-red-50 hover:text-red-500'}`}>
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirm */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
            onClick={() => setDeleteConfirm(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }}
              className={`rounded-2xl p-6 max-w-sm w-full shadow-2xl transition-colors ${isDark ? 'bg-[#1e293b] border border-[#334155]' : 'bg-white'}`}
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-500/20 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Delete Product?</h4>
                  <p className="text-xs text-gray-400">This cannot be undone</p>
                </div>
              </div>
              <p className={`text-sm mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
              </p>
              <div className="flex space-x-3">
                <button onClick={() => setDeleteConfirm(null)}
                  className={`flex-1 py-2.5 font-bold rounded-xl transition-colors text-sm ${isDark ? 'text-gray-400 hover:bg-[#334155]' : 'text-gray-500 hover:bg-gray-100'}`}>
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 py-2.5 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors text-sm flex items-center justify-center space-x-1">
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" /><span>Delete</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}